import moment from 'moment';
import * as React from 'react';
import { useEffect, useState, useRef, useCallback } from 'react';
import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import { logger } from '../../logging';
import { ContactsMigrationArgs } from './ContactsMigrationArgs';
import { InlineSwitchField, StackedField } from './WorkflowFormFields';
import { IFBWorkflowFormSelector } from '../../components/IFBWorkflowFormSelector';
import { getDAMApp } from '../../services/firebase';
import {
  ExecutionResult,
  MigrateContactsArgs,
  Workflow,
  WorkflowState,
  WorkflowSubtype,
  WorkflowSubtypeMap,
  WorkflowTypes,
  workflowTypesMap,
} from './types';
import {
  canRunWorkflowNowInModal,
  formatWorkflowModalError,
  getWorkflowDisplayStatus,
  getWorkflowErrorActionGuidance,
  getWorkflowStatusLabel,
  isWorkflowRetryableError,
  WorkflowDisplayStatus,
} from './utils';
import { PlayIcon } from '../../components/Icons';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref } from '../../utils/firebase';
import { update } from 'firebase/database';
const R = require('ramda');

const workflowsStyles = require('./workflows.css');

export enum WorkflowSchedules {
  DAILY = 'daily',
}

export const scheduleCronMap = {
  '0 0 * * *': WorkflowSchedules.DAILY,
};

const cronMap = R.invertObj(scheduleCronMap);

export const scheduleNameMap = {
  daily: 'Daily',
};

const statusBadgeToneMap = {
  [WorkflowDisplayStatus.ACTIVE]: workflowsStyles.statusBadgeGreen,
  [WorkflowDisplayStatus.PAUSED]: workflowsStyles.statusBadgeAmber,
  [WorkflowDisplayStatus.ERRORED]: workflowsStyles.statusBadgeRed,
  [WorkflowDisplayStatus.ARCHIVED]: workflowsStyles.statusBadgeNeutral,
};

const statusBadgeLabelMap = {
  [WorkflowDisplayStatus.ACTIVE]: 'Active',
  [WorkflowDisplayStatus.PAUSED]: 'Paused',
  [WorkflowDisplayStatus.ERRORED]: 'Errored',
  [WorkflowDisplayStatus.ARCHIVED]: 'Archived',
};

const SectionCard = ({
  title,
  caption,
  children,
}: {
  title: string;
  caption: string;
  children: React.ReactNode;
}) => (
  <div className={workflowsStyles.sectionCard}>
    <div className={workflowsStyles.sectionHead}>
      <p className={workflowsStyles.sectionTitle}>{title}</p>
      <p className={workflowsStyles.sectionCaption}>{caption}</p>
    </div>
    <div className={workflowsStyles.sectionBody}>{children}</div>
  </div>
);

export const WorkflowModal = ({
  workflow,
  isOpen,
  toggle,
  setWorkflow,
  user,
  isWorkflowsAdmin,
}) => {
  const damApp = getDAMApp();
  const functions = getFunctions(damApp);
  const [isNew, setIsNew] = useState(true);
  const [workflowName, setWorkflowName] = useState('');
  const [workflowDescription, setWorkflowDescription] = useState('');
  const [workflowType, setWorkflowType] = useState(
    WorkflowTypes.MIGRATE_CONTACTS
  );
  const [workflowSubtype, setWorkflowSubtype] = useState(
    WorkflowSubtype.DATA_MIGRATION
  );
  const [workflowContinuous, setWorkflowContinuous] = useState(true);
  const [workflowSchedule, setWorkflowSchedule] = useState(
    cronMap[WorkflowSchedules.DAILY]
  );
  const [workflowScheduleReadable, setWorkflowScheduleReadable] = useState(
    WorkflowSchedules.DAILY
  );
  const [workflowArgs, setWorkflowArgs] = useState<MigrateContactsArgs | null>(
    null
  );
  const [initialMigrationArgs, setInitialMigrationArgs] =
    useState<MigrateContactsArgs | null>(null);
  const [formId, setFormId] = useState('');
  const [formPublished, setFormPublished] = useState<boolean | null>(null);
  const [formTitle, setFormTitle] = useState<string | null>(null);
  const [workflowArgsValid, setWorkflowArgsValid] = useState(false);
  const [saveModal, setSaveModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [runningNow, setRunningNow] = useState(false);
  const [runNowRequested, setRunNowRequested] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [isSubtypeLocked, setIsSubtypeLocked] = useState(false);
  const [destAccType, setDestAccType] = useState();
  const [errorMessages, setErrorMessages] = useState({});
  const [discardModal, setDiscardModal] = useState(false);
  const baselineRef = useRef<string | null>(null);

  const buildFormSnapshot = (fields: {
    name: string;
    description: string;
    type: string;
    subtype: string;
    continuous: boolean;
    schedule: string;
    scheduleReadable: string;
    formId: string;
    args: any;
  }) => JSON.stringify(fields);

  const getCurrentFormSnapshot = () =>
    buildFormSnapshot({
      name: workflowName,
      description: workflowDescription,
      type: workflowType,
      subtype: workflowSubtype,
      continuous: workflowContinuous,
      schedule: workflowSchedule,
      scheduleReadable: workflowScheduleReadable,
      formId,
      args: workflowArgs,
    });

  const hasUnsavedChanges = () =>
    baselineRef.current !== null &&
    getCurrentFormSnapshot() !== baselineRef.current;

  const requestClose = () => {
    if (hasUnsavedChanges()) {
      setDiscardModal(true);
      return;
    }

    handleClose();
  };

  const confirmDiscard = () => {
    setDiscardModal(false);
    handleClose();
  };

  useEffect(() => {
    let isMounted = true;
    if (workflow === null) {
      return;
    }

    if (isMounted) {
      setIsNew(false);
      setIsDuplicate(false);
      setWorkflowName(workflow.name || '');
      setWorkflowDescription(workflow.description || '');
      setWorkflowType(workflow.type || WorkflowTypes.MIGRATE_CONTACTS);
      setWorkflowSubtype(workflow.subtype || WorkflowSubtype.DATA_MIGRATION);
      setWorkflowContinuous(workflow.continuous || true);
      setWorkflowSchedule(
        workflow.schedule || cronMap[WorkflowSchedules.DAILY]
      );
      setWorkflowScheduleReadable(
        (workflow.schedule && scheduleCronMap[workflow.schedule]) ||
          WorkflowSchedules.DAILY
      );
      setWorkflowArgs(workflow.args || null);
      setInitialMigrationArgs(workflow.args || null);
      setFormId(workflow.args?.form_id || '');
      setFormPublished(null);
      setFormTitle(null);
      setWorkflowArgsValid(false);
      setIsSubtypeLocked(
        (workflow.subtype || WorkflowSubtype.DATA_MIGRATION) ===
          WorkflowSubtype.EXILE
      );
      baselineRef.current = buildFormSnapshot({
        name: workflow.name || '',
        description: workflow.description || '',
        type: workflow.type || WorkflowTypes.MIGRATE_CONTACTS,
        subtype: workflow.subtype || WorkflowSubtype.DATA_MIGRATION,
        continuous: workflow.continuous ?? true,
        schedule: workflow.schedule || cronMap[WorkflowSchedules.DAILY],
        scheduleReadable:
          (workflow.schedule && scheduleCronMap[workflow.schedule]) ||
          WorkflowSchedules.DAILY,
        formId: workflow.args?.form_id || '',
        args: workflow.args || null,
      });
    }

    return () => {
      isMounted = false;
    };
  }, [workflow]);

  useEffect(() => {
    if (!isOpen) {
      baselineRef.current = null;
      return;
    }

    if (workflow === null && isNew && !isDuplicate) {
      baselineRef.current = buildFormSnapshot({
        name: '',
        description: '',
        type: WorkflowTypes.MIGRATE_CONTACTS,
        subtype: WorkflowSubtype.DATA_MIGRATION,
        continuous: true,
        schedule: cronMap[WorkflowSchedules.DAILY],
        scheduleReadable: WorkflowSchedules.DAILY,
        formId: '',
        args: null,
      });
    }
  }, [isOpen, workflow, isNew, isDuplicate]);

  const handleClose = () => {
    setWorkflowName('');
    setWorkflowDescription('');
    setWorkflowType(WorkflowTypes.MIGRATE_CONTACTS);
    setWorkflowSubtype(WorkflowSubtype.DATA_MIGRATION);
    setWorkflowSchedule(cronMap[WorkflowSchedules.DAILY]);
    setWorkflowScheduleReadable(WorkflowSchedules.DAILY);
    setWorkflowContinuous(true);
    setWorkflowArgs(null);
    setInitialMigrationArgs(null);
    setFormId('');
    setFormPublished(null);
    setFormTitle(null);
    setWorkflowArgsValid(false);
    setSaving(false);
    setIsDuplicate(false);
    setIsSubtypeLocked(false);
    setDestAccType(undefined);
    setDiscardModal(false);
    baselineRef.current = null;
    setWorkflow(null);
    toggle(false);
  };

  const toggleSaveModal = () => {
    setSaveModal(!saveModal);
  };

  const saveWorkflow = async () => {
    logger.debug(`saveWorkflow called - isNew: ${isNew}`);
    setSaving(true);
    const now = moment().unix();
    const validWorkflow: Workflow = {
      ...(isNew ? {} : workflow),
      name: workflowName,
      description: workflowDescription,
      type: workflowType,
      subtype: workflowSubtype,
      continuous: workflowContinuous,
      schedule: workflowSchedule,
      args: workflowArgs,
      modified: now,
    };

    if (isNew) {
      try {
        validWorkflow['created'] = now;
        validWorkflow['createdBy'] = { uid: user.uid, email: user.email };
        logger.info(
          `[saveWorkflow] saving new validWorkflow: ${JSON.stringify(
            validWorkflow
          )}`
        );
        const callable = httpsCallable(
          functions,
          'accountManager-callableCreateWorkflow'
        );
        const res = await callable(validWorkflow);
        logger.debug(
          `[saveWorkflow] create res: ${res}, ${JSON.stringify(res)}`
        );
      } catch (e) {
        logger.error(`[saveWorkflow] error saving new workflow: ${e}`);
      }
    } else {
      try {
        const workflowRef = ref(`workflows/${workflow.key}`);
        logger.info(
          `saving existing validWorkflow: ${JSON.stringify(validWorkflow)}`
        );
        await update(workflowRef, validWorkflow);
      } catch (e) {
        logger.error(`[saveWorkflow] error saving new workflow: ${e}`);
      }
    }

    logger.debug(`[saveWorkflow] about to call handleClose()`);
    toggleSaveModal();
    handleClose();
  };

  const runNow = async () => {
    if (!workflow || !workflow.key) {
      return;
    }
    setRunningNow(true);
    try {
      const callable = httpsCallable(
        functions,
        'accountManager-callableUpdateWorkflowState'
      );
      await callable({ id: workflow.key, state: WorkflowState.RUN_NOW });
      setRunNowRequested(true);
    } catch (e) {
      logger.error(`[runNow] error running workflow ${workflow.key}: ${e}`);
    }
    setRunningNow(false);
  };

  /** Switch into create mode with editable copies of the current workflow fields. */
  const duplicateWorkflow = () => {
    const copiedArgs = workflowArgs ? { ...workflowArgs } : null;
    const copiedName = `${workflowName} (copy)`;

    setInitialMigrationArgs(copiedArgs);
    setWorkflow(null);
    setIsNew(true);
    setIsDuplicate(true);
    setIsSubtypeLocked(false);
    setDestAccType(undefined);
    setWorkflowName(copiedName);
    if (copiedArgs) {
      setWorkflowArgs(copiedArgs);
      // Keep formPublished; formId is unchanged so the selector won't re-fetch.
      // Validity is recomputed by ContactsMigrationArgs once args hydrate.
      setWorkflowArgsValid(false);
    }

    baselineRef.current = buildFormSnapshot({
      name: copiedName,
      description: workflowDescription,
      type: workflowType,
      subtype: workflowSubtype,
      continuous: workflowContinuous,
      schedule: workflowSchedule,
      scheduleReadable: workflowScheduleReadable,
      formId,
      args: copiedArgs,
    });
  };

  const handleScheduleChange = (e) => {
    const scheduleReadable = e.target.value;
    setWorkflowScheduleReadable(scheduleReadable);
    setWorkflowSchedule(cronMap[scheduleReadable]);
  };

  const handleSetWorkflowArgs = useCallback((e) => {
    logger.debug(`[handleSetWorkflowArgs] e: ${JSON.stringify(e)}`);
    setWorkflowArgs((prev) => ({ ...(prev || {}), ...e }));
  }, []);

  const handleSetWorkflowType = (e) => {
    const type = e.target.value;

    switch (type) {
      case WorkflowTypes.MIGRATE_CONTACTS:
        setWorkflowScheduleReadable(WorkflowSchedules.DAILY);
        setWorkflowSchedule(cronMap[WorkflowSchedules.DAILY]);
        break;
      default:
    }

    setWorkflowType(type);
  };

  const handleSetDestAccType = (accountType) => {
    setDestAccType(accountType);
    setIsSubtypeLocked(accountType === 'exile');
    if (accountType === 'exile') setWorkflowSubtype(WorkflowSubtype.EXILE);
  };

  const handleSetWorkflowSubtype = (e) => {
    const subtype = e.target.value;

    setWorkflowSubtype(subtype);
  };

  useEffect(() => {
    if (!destAccType) return;

    const newMessage: any = { ...errorMessages };
    if (workflowSubtype === WorkflowSubtype.EXILE && destAccType !== 'exile') {
      newMessage['INVALID_SUBTYPE'] = {
        message:
          'Exile subtype can only be used when the destination is an exile account',
      };
    } else {
      delete newMessage['INVALID_SUBTYPE'];
    }

    setErrorMessages(newMessage);
  }, [destAccType, workflowSubtype]);

  const isArchived: boolean = (workflow && workflow.isArchived) || false;
  const createdBy = (workflow && workflow.createdBy) || {
    uid: user.uid,
    email: user.email,
  };
  // Display-only — used in read-only Created By field on existing workflows
  const createdByEmail = createdBy?.email ?? '';
  const hasAnyErrors = Object.keys(errorMessages).length > 0;
  const isWorkflowNameValid = workflowName !== '';
  const saveDisabled =
    !workflowArgsValid ||
    !isWorkflowNameValid ||
    isArchived ||
    hasAnyErrors;
  const scheduleDisabled =
    (!isNew && !isDuplicate) || workflowType === WorkflowTypes.MIGRATE_CONTACTS;
  const formFieldsEditable = isNew || isDuplicate;

  const displayStatus = workflow ? getWorkflowDisplayStatus(workflow) : null;
  const showRunNow = workflow ? canRunWorkflowNowInModal(workflow) : false;
  const runNowDisabled = !showRunNow || runningNow;
  const isRetryableExecutionError =
    !!workflow &&
    workflow.last_execution_result === ExecutionResult.ERROR &&
    isWorkflowRetryableError(workflow.error);

  const modalTitle = isNew
    ? isDuplicate
      ? 'Duplicate workflow'
      : 'Create workflow'
    : `${isArchived ? '[ARCHIVED] ' : ''}Edit workflow`;

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={requestClose}
        className={workflowsStyles.workflowModal}
      >
      <ModalHeader
        toggle={requestClose}
        className={workflowsStyles.workflowModalHeader}
        tag="div"
      >
        <div className={workflowsStyles.modalTitleRow}>
          <h5 className={workflowsStyles.modalTitle}>{modalTitle}</h5>
          {workflow && displayStatus && (
            <span
              className={`${workflowsStyles.statusBadge} ${statusBadgeToneMap[displayStatus]}`}
            >
              {statusBadgeLabelMap[displayStatus]}
            </span>
          )}
        </div>
        {workflow && (
          <div className={workflowsStyles.modalSubtitle}>
            {`${workflow.name} • ${workflow.key}`}
          </div>
        )}
      </ModalHeader>
      <ModalBody className={workflowsStyles.workflowModalBody}>
        {/* Execution alerts — last_execution_result drives display; status may still be enabled */}
        {runNowRequested && (
          <Alert color="success" toggle={() => setRunNowRequested(false)}>
            Run requested — this workflow has been queued to run now.
          </Alert>
        )}
        {workflow?.last_execution_result === ExecutionResult.RUNNING && (
          <Alert color="info">
            <strong>Migration in progress</strong> — this workflow is currently
            running. Contact counts may decrease while migration is active.
          </Alert>
        )}
        {workflow?.last_execution_result === ExecutionResult.ERROR && (
          <Alert color={isRetryableExecutionError ? 'warning' : 'danger'}>
            <strong>
              {isRetryableExecutionError
                ? 'Temporary issue — retrying'
                : 'Last run failed'}
            </strong>
            {` — ${formatWorkflowModalError(
              workflow.error,
              formTitle,
              formId || workflow.args?.form_id
            )}`}
            <div className="mt-2">
              {getWorkflowErrorActionGuidance(workflow.error)}
            </div>
            {!isRetryableExecutionError && workflow.status === 'paused' && (
              <div className="mt-2">
                This workflow was paused automatically because of the error
                above — it was not paused by an operator.
              </div>
            )}
            {!isRetryableExecutionError && workflow.status === 'enabled' && (
              <div className="mt-2">
                The scheduler is still enabled — some contacts may not have
                migrated; check source/destination counts and logs.
              </div>
            )}
          </Alert>
        )}
        {errorMessages && Object.keys(errorMessages).length > 0
          ? R.map((errorMessage: any) => {
              return (
                <Alert color="danger" key={errorMessage}>
                  {errorMessage && errorMessages[errorMessage].message}
                </Alert>
              );
            }, Object.keys(errorMessages))
          : null}
        <Form>
          {isNew === false && workflow ? (
            <SectionCard
              title="Workflow details"
              caption="System information about this workflow."
            >
              <Row>
                <Col sm={6}>
                  <StackedField label="ID" htmlFor="id">
                    <Input
                      type="text"
                      value={workflow.key}
                      id="id"
                      disabled={true}
                    />
                  </StackedField>
                  <StackedField label="Status" htmlFor="status">
                    <Input
                      type="select"
                      value={getWorkflowStatusLabel(workflow)}
                      id="status"
                      disabled={true}
                    >
                      <option value={getWorkflowStatusLabel(workflow)}>
                        {getWorkflowStatusLabel(workflow)}
                      </option>
                    </Input>
                  </StackedField>
                  <StackedField label="Created" htmlFor="created">
                    <Input
                      type="text"
                      value={
                        workflow.created
                          ? moment
                              .unix(parseInt(workflow.created))
                              .format('YYYY-MM-DD HH:mm:ss')
                          : ''
                      }
                      id="created"
                      disabled={true}
                    />
                  </StackedField>
                </Col>
                <Col sm={6}>
                  <StackedField label="Created by" htmlFor="created_by">
                    <Input
                      type="text"
                      value={createdByEmail}
                      id="created_by"
                      disabled={true}
                    />
                  </StackedField>
                  <StackedField label="Modified" htmlFor="modified">
                    <Input
                      type="text"
                      value={
                        workflow.modified
                          ? moment
                              .unix(parseInt(workflow.modified))
                              .format('YYYY-MM-DD HH:mm:ss')
                          : ''
                      }
                      id="modified"
                      disabled={true}
                    />
                  </StackedField>
                </Col>
              </Row>
            </SectionCard>
          ) : null}
          <SectionCard
            title="General settings"
            caption="Configure the basic properties and schedule of this workflow."
          >
            <Row>
              <Col sm={6}>
                <StackedField label="Name" required htmlFor="name">
                  <Input
                    type="text"
                    value={workflowName}
                    id="name"
                    onChange={(e) => setWorkflowName(e.target.value)}
                    disabled={isArchived}
                  />
                </StackedField>
                <StackedField label="Description" htmlFor="description" alignTop>
                  <Input
                    type="textarea"
                    value={workflowDescription}
                    id="description"
                    placeholder="Optional description for this workflow..."
                    onChange={(e) => setWorkflowDescription(e.target.value)}
                    disabled={isArchived}
                  />
                </StackedField>
                <StackedField label="Type" required htmlFor="type">
                  <Input
                    type="select"
                    value={workflowType}
                    id="type"
                    disabled={!formFieldsEditable}
                    onChange={handleSetWorkflowType}
                  >
                    <option
                      value={WorkflowTypes.MIGRATE_CONTACTS}
                      key={WorkflowTypes.MIGRATE_CONTACTS}
                    >
                      {workflowTypesMap.migrate_contacts}
                    </option>
                  </Input>
                </StackedField>
                <StackedField label="Subtype" required htmlFor="subtype">
                  <Input
                    type="select"
                    value={workflowSubtype}
                    id="subtype"
                    disabled={!formFieldsEditable || isSubtypeLocked}
                    onChange={handleSetWorkflowSubtype}
                  >
                    <option
                      value={WorkflowSubtype.DATA_MIGRATION}
                      key={WorkflowSubtype.DATA_MIGRATION}
                    >
                      {WorkflowSubtypeMap.data_migration}
                    </option>
                    <option
                      value={WorkflowSubtype.EXILE}
                      key={WorkflowSubtype.EXILE}
                    >
                      {WorkflowSubtypeMap.exile}
                    </option>
                    <option
                      value={WorkflowSubtype.DATA_TRANSFER}
                      key={WorkflowSubtype.DATA_TRANSFER}
                    >
                      {WorkflowSubtypeMap.data_transfer}
                    </option>
                  </Input>
                </StackedField>
                <InlineSwitchField
                  label="Continuous"
                  caption="Keep workflow running on schedule"
                  id="continuous"
                  checked={workflowContinuous}
                  onChange={setWorkflowContinuous}
                  disabled={true}
                />
                {workflow && workflow.endDate ? (
                  <StackedField label="End date" htmlFor="endDate">
                    <Input
                      type="date"
                      value={moment
                        .unix(parseInt(workflow.endDate))
                        .format('YYYY-MM-DD')}
                      id="endDate"
                      disabled={true}
                    />
                  </StackedField>
                ) : null}
              </Col>
              <Col sm={6}>
                {workflowContinuous && (
                  <StackedField label="Schedule" required htmlFor="schedule">
                    <Input
                      type="select"
                      value={workflowScheduleReadable}
                      id="schedule"
                      disabled={scheduleDisabled}
                      onChange={handleScheduleChange}
                    >
                      <option value="" key="">
                        Please select
                      </option>
                      <option
                        value={WorkflowSchedules.DAILY}
                        key={WorkflowSchedules.DAILY}
                      >
                        {scheduleNameMap[WorkflowSchedules.DAILY]}
                      </option>
                    </Input>
                  </StackedField>
                )}
                {workflowContinuous && (
                  <StackedField
                    label="Schedule CRON"
                    caption="Use standard CRON format for advanced scheduling."
                    htmlFor="workflowSchedule"
                  >
                    <Input
                      type="text"
                      value={workflowSchedule}
                      id="workflowSchedule"
                      disabled={true}
                    />
                  </StackedField>
                )}
                {workflowType === WorkflowTypes.MIGRATE_CONTACTS && (
                  <StackedField label="IFB Form" required htmlFor="form">
                    <IFBWorkflowFormSelector
                      formId={formId}
                      setFormId={setFormId}
                      disabled={!formFieldsEditable}
                      onPublishedChange={setFormPublished}
                      onFormTitleChange={setFormTitle}
                    />
                  </StackedField>
                )}
                {workflowType === WorkflowTypes.MIGRATE_CONTACTS && (
                  <>
                    <InlineSwitchField
                      label="Is Privacy Workflow"
                      caption="Source or destination account is exempt from privacy workflow."
                      id="isPrivacy"
                      checked={workflowArgs?.is_privacy || false}
                      disabled={true}
                    />
                    <InlineSwitchField
                      label="Is Store Workflow"
                      caption="Source account is stored for future reference."
                      id="isStore"
                      checked={workflowArgs?.is_store || false}
                      disabled={true}
                    />
                  </>
                )}
              </Col>
            </Row>
          </SectionCard>
          {workflowType === WorkflowTypes.MIGRATE_CONTACTS && (
            <ContactsMigrationArgs
              initialArgs={initialMigrationArgs}
              formId={formId}
              setFormId={setFormId}
              setWorkflowArgs={handleSetWorkflowArgs}
              setWorkflowArgsValid={setWorkflowArgsValid}
              setDestAccType={handleSetDestAccType}
              disabled={!formFieldsEditable}
              formPublished={formPublished}
            />
          )}
          <div className={workflowsStyles.modalFooterBar}>
            <div className={workflowsStyles.footerLeft}>
              {isNew === false && workflow && (
                <>
                  {showRunNow && (
                    <Button
                      outline
                      color="secondary"
                      disabled={runNowDisabled}
                      onClick={runNow}
                    >
                      <span className={workflowsStyles.buttonIcon}>
                        <PlayIcon />
                      </span>
                      Run now
                    </Button>
                  )}
                  <Button outline color="secondary" onClick={duplicateWorkflow}>
                    Duplicate
                  </Button>
                </>
              )}
            </div>
            <div className={workflowsStyles.footerRight}>
              <Button color="secondary" outline onClick={requestClose}>
                Cancel
              </Button>
              <Button
                color="primary"
                disabled={saveDisabled}
                onClick={toggleSaveModal}
              >
                {isNew ? 'Create workflow' : 'Save changes'}
              </Button>
            </div>
          </div>
        </Form>
      </ModalBody>
    </Modal>
    <Modal isOpen={discardModal} toggle={() => setDiscardModal(false)}>
      <ModalHeader toggle={() => setDiscardModal(false)}>
        Discard unsaved changes?
      </ModalHeader>
      <ModalBody>
        You have unsaved changes. Close without saving?
      </ModalBody>
      <ModalFooter>
        <Button color="danger" onClick={confirmDiscard}>
          Discard changes
        </Button>{' '}
        <Button color="secondary" onClick={() => setDiscardModal(false)}>
          Keep editing
        </Button>
      </ModalFooter>
    </Modal>
    <Modal isOpen={saveModal} toggle={toggleSaveModal}>
      <ModalHeader toggle={toggleSaveModal}>Please Confirm Save</ModalHeader>
      <ModalBody>{`Please confirm that you'd like to save the workflow.`}</ModalBody>
      <ModalFooter>
        <Button
          color="primary"
          onClick={async () => await saveWorkflow()}
          disabled={saving}
        >
          Confirm
        </Button>{' '}
        <Button color="secondary" onClick={toggleSaveModal} disabled={saving}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
    </>
  );
};
